import { useState, useRef, useEffect } from 'react';
import { Upload, Server, RefreshCw, FileText, Zap, Eye, Sliders } from 'react-feather';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { segmentationBuilderApi } from './segmentation-api';

interface SegmentationUploadProps {
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.SegmentationUpload;
}

// --- Animations ---

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const rotateBorder = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const spinAnim = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// --- Styled Components ---

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid #e5e5e5;
  border-top-color: #28aae1;
  border-radius: 50%;
  animation: ${spinAnim} 0.8s linear infinite;
  margin: 0 auto;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 750px;
  margin: 0 auto;
  width: 100%;
`;

const HeroSection = styled.div`
  text-align: center;
  padding: 36px 28px 28px;
  padding-bottom: 0px;
  padding-top: 0px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  position: relative;
  overflow: hidden;
`;

const HeroIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  box-shadow:
    0 8px 32px rgba(40, 170, 225, 0.2),
    0 2px 8px rgba(0, 0, 0, 0.06);
  animation: ${float} 3s ease-in-out infinite;
  color: #28aae1;
`;

const Title = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: #323232;
  margin-bottom: 8px;
  letter-spacing: -0.3px;
`;

const Subtitle = styled.p`
  color: #626262;
  font-size: 14px;
  max-width: 420px;
  margin: 0 auto 20px;
  line-height: 1.6;
`;

const FeatureBadges = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Badge = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 600;
  color: #1d94c7;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(4px);
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid rgba(40, 170, 225, 0.2);
`;

const ModeTabs = styled.div`
  display: flex;
  border-radius: 10px;
  background: #f2f2f2;
  padding: 4px;
  gap: 4px;
`;

const ModeTab = styled.button<{ $active: boolean }>`
  flex: 1;
  font-size: 13px;
  padding: 9px 0;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.25s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: ${(p) => (p.$active ? '#ffffff' : 'transparent')};
  color: ${(p) => (p.$active ? '#28AAE1' : '#626262')};
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  box-shadow: ${(p) => (p.$active ? '0 2px 8px rgba(0,0,0,0.08)' : 'none')};
  &:hover {
    color: ${(p) => (p.$active ? '#28AAE1' : '#323232')};
  }
`;

const FileCard = styled.div<{ $selected: boolean; $disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 10px;
  padding: 14px 16px;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  border: 1px solid ${(p) => (p.$selected ? '#28AAE1' : '#e5e5e5')};
  background: ${(p) => (p.$selected ? '#E5F5FC' : '#ffffff')};
  color: #323232;
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  transition: all 0.2s;
  box-shadow: ${(p) => (p.$selected ? '0 2px 12px rgba(40,170,225,0.15)' : '0 1px 3px rgba(0,0,0,0.04)')};
  &:hover {
    border-color: ${(p) => (p.$selected ? '#28AAE1' : '#ADADAD')};
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
`;

const FileIcon = styled.div<{ $selected: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(p) => (p.$selected ? '#28AAE1' : '#f2f2f2')};
  color: ${(p) => (p.$selected ? '#ffffff' : '#ADADAD')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DropZoneOuter = styled.div<{ $dragging: boolean; $disabled: boolean }>`
  border-radius: 16px;
  padding: 2px;
  background: ${(p) =>
    p.$dragging
      ? 'linear-gradient(90deg, #28AAE1, #76d1ff, #565add, #28AAE1)'
      : 'linear-gradient(90deg, #d3d3d3, #e5e5e5, #d3d3d3)'};
  background-size: 300% 100%;
  animation: ${rotateBorder} 3s ease infinite;
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  pointer-events: ${(p) => (p.$disabled ? 'none' : 'auto')};
  cursor: pointer;
  transition: all 0.3s;
  &:hover {
    background: linear-gradient(90deg, #28aae1, #76d1ff, #565add, #28aae1);
    background-size: 300% 100%;
  }
`;

const DropZoneInner = styled.div`
  background: #ffffff;
  border-radius: 14px;
  padding: 40px 32px;
  text-align: center;
`;

const DropIcon = styled.div<{ $uploading: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${(p) => (p.$uploading ? '#E5F5FC' : '#f2f2f2')};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: ${(p) => (p.$uploading ? '#28AAE1' : '#ADADAD')};
  transition: all 0.3s;
  ${(p) =>
    p.$uploading &&
    css`
      animation: ${float} 1.5s ease-in-out infinite;
    `}
`;

const DropTitle = styled.p`
  color: #323232;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 6px;
`;

const DropSub = styled.p`
  color: #adadad;
  font-size: 13px;
`;

const DropFormats = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
`;

const FormatChip = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #adadad;
  background: #f2f2f2;
  padding: 3px 10px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ErrorBox = styled.div`
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 13px;
  background: #fde5e5;
  border: 1px solid #E12E40;
  color: #c62828;
`;

const FieldGroup = styled.div<{ $flex?: number }>`
  flex: ${(p) => p.$flex ?? 1};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #323232;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  font-size: 13px;
  color: #323232;
  font-family: inherit;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s;
  &::placeholder {
    color: #adadad;
  }
  &:focus {
    border-color: #28aae1;
  }
  &:hover {
    border-color: #28aae1;
  }
`;

const QualPanel = styled.div`
  border: 1px solid rgba(76, 184, 71, 0.4);
  border-radius: 12px;
  overflow: hidden;
`;

const QualHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(76, 184, 71, 0.07);
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: rgba(76, 184, 71, 0.12);
  }
`;

const QualHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const QualTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #4cb847;
`;

const QualCountBadge = styled.span`
  font-size: 11px;
  background: rgba(76, 184, 71, 0.18);
  color: #2d7a2e;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
`;

const QualChevron = styled.span<{ $open: boolean }>`
  font-size: 11px;
  color: #adadad;
  transition: transform 0.2s;
  display: inline-block;
  transform: ${(p) => (p.$open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const QualBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: rgba(0, 0, 0, 0.01);
`;

const QualDescription = styled.p`
  font-size: 12px;
  color: #adadad;
  line-height: 1.5;
  margin: 0;
`;

const QualSelectActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const QualSelectLabel = styled.span`
  font-size: 12px;
  color: #adadad;
  font-weight: 500;
`;

const QualSelectActions = styled.div`
  display: flex;
  gap: 8px;
`;

const QualLinkBtn = styled.button`
  font-size: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: #4cb847;
  &:hover {
    color: #2d7a2e;
  }
`;

const QualClearBtn = styled.button`
  font-size: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: #adadad;
  &:hover {
    color: #626262;
  }
`;

const QualSearchInput = styled.input`
  width: 100%;
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  font-size: 13px;
  color: #323232;
  font-family: inherit;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s;
  &::placeholder {
    color: #adadad;
  }
  &:focus {
    border-color: #4cb847;
  }
`;

const QualFileList = styled.div`
  max-height: 288px;
  overflow-y: auto;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const QualFileLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.15s;
  &:hover {
    background: #f5f5f5;
  }
`;

const QualFileName = styled.span`
  color: #323232;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QualSummaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(76, 184, 71, 0.8);
  background: rgba(76, 184, 71, 0.06);
  border-radius: 8px;
  padding: 8px 12px;
`;

const ProceedButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #28aae1;
  color: #ffffff;
  transition: all 0.2s;
  box-shadow: 0 4px 16px rgba(40, 170, 225, 0.3);
  &:hover:not(:disabled) {
    background: #1d94c7;
    transform: translateY(-1px);
  }
  &:disabled {
    background: #e5e5e5;
    color: #adadad;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

interface ServerFileItem {
  name: string;
  size_bytes: number;
}

export function SegmentationUpload({ queryId, queryIdentifier, value }: SegmentationUploadProps) {
  const [mode, setMode] = useState<'server' | 'upload'>('server');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverFiles, setServerFiles] = useState<ServerFileItem[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>('Claims_masked_file.xlsx');
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [projectTitle, setProjectTitle] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // Qual transcript state
  const [qualExpanded, setQualExpanded] = useState(false);
  const [qualServerFiles, setQualServerFiles] = useState<Array<{ path: string; filename: string }>>([]);
  const [qualSelected, setQualSelected] = useState<Set<string>>(new Set());
  const [qualSearch, setQualSearch] = useState('');
  const [qualLoading, setQualLoading] = useState(false);

  // Mock server files for now - in real impl, fetch from API
  useEffect(() => {
    setLoadingFiles(true);
    // Simulate API call
    setTimeout(() => {
      setServerFiles([
        { name: 'Claims_masked_file.xlsx', size_bytes: 5242880 },
      ]);
      setLoadingFiles(false);
    }, 500);

    // Mock qual files
    setQualServerFiles(value.qualFiles || []);
  }, [value.qualFiles]);

  const stageUploadFile = (file: File | undefined): void => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !['xlsx', 'xls', 'csv'].includes(ext)) {
      setError('Please upload an .xlsx or .csv file');
      setPendingUploadFile(null);
      return;
    }
    setPendingUploadFile(file);
    setSelectedFile(null);
    setError(null);
  };

  const handleProceed = async (): Promise<void> => {
    if (!projectTitle.trim()) {
      setError('Please enter a project title');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const qualFiles = [...qualSelected]
        .map((path) => {
          const f = qualServerFiles.find((sf) => sf.path === path);
          return f ? { path: f.path, filename: f.filename } : null;
        })
        .filter((f): f is { path: string; filename: string } => f !== null);

      // Step 1: Upload file (multipart) or select server file → get sessionId
      const { sessionId } = await segmentationBuilderApi.uploadFile(
        pendingUploadFile || undefined,
        selectedFile || undefined,
      );

      const uploadedFilename = pendingUploadFile?.name || selectedFile || '';

      // Step 2: Resume workflow with metadata only (no File object)
      await segmentationBuilderApi.submitUploadComplete(queryIdentifier, {
        sessionId,
        uploadedFile: uploadedFilename,
        qualFiles,
        projectTitle: projectTitle.trim(),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Wrapper>
      <HeroSection>
        <HeroIcon>
          <Upload size={30} strokeWidth={2} />
        </HeroIcon>
        <Title>Segment Generator</Title>
        <Subtitle>
          Upload your claims data and let AI analyze, engineer features, and build actionable HCP segments — with
          full transparency and override control.
        </Subtitle>
        <FeatureBadges>
          <Badge>
            <Zap size={12} /> AI-Powered
          </Badge>
          <Badge>
            <Eye size={12} /> Full Transparency
          </Badge>
          <Badge>
            <Sliders size={12} /> Override Control
          </Badge>
        </FeatureBadges>
      </HeroSection>

      <FieldGroup $flex={2}>
        <FieldLabel>
          Project Title <span style={{ color: '#E12E40' }}>*</span>
        </FieldLabel>
        <FieldInput
          type="text"
          value={projectTitle}
          onChange={(e) => setProjectTitle(e.target.value)}
          placeholder="e.g., Q1 2024 HCP Segmentation"
        />
      </FieldGroup>

      {(serverFiles.length > 0 || loadingFiles) && (
        <ModeTabs>
          <ModeTab $active={mode === 'server'} onClick={() => setMode('server')}>
            <Server size={14} /> Existing Datasets
          </ModeTab>
          <ModeTab $active={mode === 'upload'} onClick={() => setMode('upload')}>
            <Upload size={14} /> Upload New
          </ModeTab>
        </ModeTabs>
      )}

      {mode === 'server' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loadingFiles ? (
            <div
              style={{
                padding: '32px 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <LoadingSpinner />
              <p style={{ color: '#ADADAD', fontSize: 13 }}>Loading datasets...</p>
            </div>
          ) : serverFiles.length === 0 ? (
            <p style={{ color: '#ADADAD', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
              No data files found on server
            </p>
          ) : (
            serverFiles.map((f) => (
              <FileCard
                key={f.name}
                $selected={selectedFile === f.name}
                $disabled={uploading}
                onClick={() => {
                  if (!uploading) {
                    setSelectedFile(f.name);
                    setPendingUploadFile(null);
                  }
                }}
              >
                <FileIcon $selected={selectedFile === f.name}>
                  <FileText size={18} />
                </FileIcon>
                <FileInfo>
                  <p
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#323232',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {f.name}
                    <span
                      style={{
                        fontSize: 10,
                        background: '#E5F5FC',
                        color: '#1D94C7',
                        padding: '2px 6px',
                        borderRadius: 4,
                        fontWeight: 600,
                      }}
                    >
                      Preprocessed
                    </span>
                  </p>
                  <p style={{ fontSize: 13, color: '#ADADAD', marginTop: 2 }}>
                    {(f.size_bytes / 1024 / 1024).toFixed(1)} MB
                  </p>
                </FileInfo>
              </FileCard>
            ))
          )}
        </div>
      )}

      {mode === 'upload' && (
        <DropZoneOuter
          $dragging={dragging}
          $disabled={uploading}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            stageUploadFile(e.dataTransfer.files[0]);
          }}
          onClick={() => fileRef.current?.click()}
        >
          <DropZoneInner>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={(e) => {
                stageUploadFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <DropIcon $uploading={uploading}>
              {uploading ? <RefreshCw size={24} /> : <Upload size={24} />}
            </DropIcon>
            {pendingUploadFile ? (
              <>
                <DropTitle style={{ color: '#28AAE1' }}>{pendingUploadFile.name}</DropTitle>
                <DropSub>{(pendingUploadFile.size / 1024 / 1024).toFixed(1)} MB — click to change</DropSub>
              </>
            ) : (
              <>
                <DropTitle>Drop your claims file here</DropTitle>
                <DropSub>or click to browse</DropSub>
                <DropFormats>
                  <FormatChip>.xlsx</FormatChip>
                  <FormatChip>.xls</FormatChip>
                  <FormatChip>.csv</FormatChip>
                </DropFormats>
              </>
            )}
          </DropZoneInner>
        </DropZoneOuter>
      )}

      {error && <ErrorBox>{error}</ErrorBox>}

      {/* Qualitative Transcripts Panel */}
      <QualPanel>
        <QualHeader type="button" onClick={() => setQualExpanded(!qualExpanded)}>
          <QualHeaderLeft>
            <QualTitle>Qualitative Transcripts</QualTitle>
            {qualSelected.size > 0 && <QualCountBadge>{qualSelected.size} selected</QualCountBadge>}
          </QualHeaderLeft>
          <QualChevron $open={qualExpanded}>▼</QualChevron>
        </QualHeader>

        {qualExpanded && (
          <QualBody>
            <QualDescription>
              Interview transcripts and research documents enrich your segmentation with qualitative insights.
            </QualDescription>

            {qualLoading ? (
              <p style={{ color: '#adadad', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>
                Loading transcript files…
              </p>
            ) : qualServerFiles.length > 0 ? (
              <>
                <QualSelectActionsRow>
                  <QualSelectLabel>
                    Server Transcripts ({qualSelected.size}/{qualServerFiles.length})
                  </QualSelectLabel>
                  <QualSelectActions>
                    <QualLinkBtn
                      type="button"
                      onClick={() => setQualSelected(new Set(qualServerFiles.map((f) => f.path)))}
                    >
                      Select All
                    </QualLinkBtn>
                    <QualClearBtn type="button" onClick={() => setQualSelected(new Set())}>
                      Clear
                    </QualClearBtn>
                  </QualSelectActions>
                </QualSelectActionsRow>

                {qualServerFiles.length > 8 && (
                  <QualSearchInput
                    type="text"
                    placeholder="Search transcripts…"
                    value={qualSearch}
                    onChange={(e) => setQualSearch(e.target.value)}
                  />
                )}

                <QualFileList>
                  {qualServerFiles
                    .filter((f) => !qualSearch || f.filename.toLowerCase().includes(qualSearch.toLowerCase()))
                    .map((f) => (
                      <QualFileLabel key={f.path}>
                        <input
                          type="checkbox"
                          checked={qualSelected.has(f.path)}
                          onChange={() => {
                            const next = new Set(qualSelected);
                            if (next.has(f.path)) {
                              next.delete(f.path);
                            } else {
                              next.add(f.path);
                            }
                            setQualSelected(next);
                          }}
                        />
                        <QualFileName>{f.filename}</QualFileName>
                      </QualFileLabel>
                    ))}
                  {qualSearch &&
                    qualServerFiles.filter((f) => f.filename.toLowerCase().includes(qualSearch.toLowerCase()))
                      .length === 0 && (
                      <p
                        style={{
                          color: '#adadad',
                          fontSize: 12,
                          textAlign: 'center',
                          padding: '8px 0',
                        }}
                      >
                        No files match "{qualSearch}"
                      </p>
                    )}
                </QualFileList>

                {qualSelected.size > 0 && (
                  <QualSummaryRow>
                    <span style={{ fontWeight: 600 }}>{qualSelected.size}</span>
                    <span>file{qualSelected.size !== 1 ? 's' : ''} ready — processed during analysis</span>
                  </QualSummaryRow>
                )}
              </>
            ) : (
              <p style={{ color: '#adadad', fontSize: 13, textAlign: 'center', padding: '8px 0' }}>
                No transcript files found on server
              </p>
            )}
          </QualBody>
        )}
      </QualPanel>

      <ProceedButton
        type="button"
        disabled={uploading || (selectedFile === null && pendingUploadFile === null) || !projectTitle.trim()}
        onClick={() => {
          void handleProceed();
        }}
      >
        {uploading
          ? 'Processing...'
          : selectedFile !== null || pendingUploadFile !== null
            ? `Proceed with Claims Data${
                qualSelected.size > 0 ? ` + ${qualSelected.size} Transcript${qualSelected.size !== 1 ? 's' : ''}` : ''
              }`
            : 'Select a claims data file to proceed'}
      </ProceedButton>
    </Wrapper>
  );
}
